import React from 'react'

import { useTranslation } from 'react-i18next'
import { Form, useLoaderData, useNavigation, useSearchParams, useSubmit } from 'react-router-dom'

import LoadingMessage from '@components/LoadingMessage'
import ChartCard from '@components/myScores/ChartCard'
import Dropdown from '@components/shared/form/Dropdown'
import { usePageTitle } from '@providers/LayoutProvider'

import type { DashboardLoaderResponse } from '@pages/home/loaders/dashboard.loader'

const ITEMS = [
  { name: 'period.last30Days', queryValue: '30d' },
  { name: 'period.last3Months', queryValue: '3m' },
  { name: 'period.last6Months', queryValue: '6m' },
  { name: 'period.last12Months', queryValue: '12m' },
]

const DashboardPage: React.FC = () => {
  const { t } = useTranslation()
  const data = useLoaderData() as DashboardLoaderResponse
  const navigation = useNavigation()
  const submit = useSubmit()
  const [searchParams] = useSearchParams()

  usePageTitle(t('titles.dashboard'))

  const isReloading =
    navigation.state === 'loading' &&
    navigation.formData !== null &&
    navigation.location.pathname === '/home/dashboard'

  return (
    <>
      {isReloading && <LoadingMessage />}
      <Form className="w-full flex flex-row items-center justify-end h-16" role="search">
        <Dropdown
          variant="sm"
          name="period"
          borderless
          onChange={(e) => {
            const isFirstSearch = !searchParams.get('period')
            submit(e.currentTarget.form, {
              replace: !isFirstSearch,
            })
          }}
          className="text-base"
          value={data.period}>
          {ITEMS.map(({ name, queryValue }) => (
            <option key={name} value={queryValue}>
              {t(name)}
            </option>
          ))}
        </Dropdown>
      </Form>
      <div
        data-testid="dashboard-page"
        className="grid gap-4 flex-1 relative mb-4 overflow-y-auto min-h-0"
        style={{
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(382px, 100%), 1fr))',
          gridAutoRows: 'min-content',
        }}>
        <ChartCard data={data.viewsByScore} title={t('content.viewsByScore')} icon="eye-show" />
        <ChartCard
          data={data.viewsByTimeframe}
          title={t('content.viewsByTimeframe')}
          icon="eye-show"
          timeIncrement={data.timeIncrement}
        />
        <ChartCard
          data={data.commentsByScore}
          title={t('content.commentsByScore')}
          icon="feedback"
        />
        <ChartCard
          data={data.commentsByTimeframe}
          title={t('content.commentsByTimeframe')}
          icon="feedback"
          timeIncrement={data.timeIncrement}
        />
        <ChartCard
          data={data.favoritesByScore}
          title={t('content.favoritesByScore')}
          icon="favorite-outline"
        />
        <ChartCard
          data={data.favoritesByTimeframe}
          title={t('content.favoritesByTimeframe')}
          icon="favorite-outline"
          timeIncrement={data.timeIncrement}
        />
      </div>
    </>
  )
}

export default DashboardPage
